import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [state, setState] = useState({ status: "checking", email: null, reason: null });
  const [pw, setPw] = useState({ p1: "", p2: "" });
  const [busy, setBusy] = useState(false);

  // Preflight — verify the token before we render the form so users hit an
  // "invalid / expired" message instantly instead of after submitting.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/auth/password/reset/verify?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        if (r.data.valid) setState({ status: "ready", email: r.data.email, reason: null });
        else setState({ status: "invalid", email: null, reason: r.data.reason || "invalid" });
      } catch {
        if (!alive) return;
        setState({ status: "invalid", email: null, reason: "invalid" });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.p1.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw.p1 !== pw.p2) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/password/reset", { token, new_password: pw.p1 });
      // Backend logs the user in — store the new JWT and route straight to the
      // dashboard so the user never has to re-type the fresh password.
      if (r?.data?.token && setSession) {
        setSession({ token: r.data.token, email: r.data.email });
      } else if (r?.data?.token) {
        // Fallback for older AuthContext without setSession — writing the same
        // key the axios client + api.js already read on boot.
        localStorage.setItem("sn_token", r.data.token);
      }
      toast.success("Password updated. Logged in.");
      nav("/app/overview");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Reset failed. Try requesting a new link.";
      toast.error(msg);
      // If the server says the link is no longer valid, flip the UI so the
      // user isn't stuck on a form that will never accept them.
      if ((err?.response?.status || 0) === 400) {
        setState({ status: "invalid", email: null, reason: "expired" });
      }
    } finally { setBusy(false); }
  };

  const reasonCopy = {
    invalid: "This reset link is not valid.",
    expired: "This reset link has expired.",
    used:    "This reset link has already been used.",
  };

  return (
    <>
      <SEO title="Reset password — SerpNex" description="Set a new password for your SerpNex account." canonical="/reset-password" />
      <div className="min-h-screen grid md:grid-cols-2 bg-white">
        <div className="hidden md:flex flex-col justify-between p-14 bg-[#0A0D14] text-white relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30" style={{ filter: 'invert(1)' }} />
          <Logo variant="dark" size="lg" />
          <div className="relative">
            <div className="eyebrow" style={{ color: '#7EA1FF' }}>Choose a new password</div>
            <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold">Almost done.<br />Pick something<br />strong.</h1>
            <p className="mt-6 text-white/60 max-w-md">At least 8 characters. Mix letters, numbers, and a symbol for the strongest protection — you can update it anytime from your account settings.</p>
          </div>
          <div className="text-xs text-white/40 relative">Single-use link · You&apos;ll be logged in automatically after reset</div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-14">
          {state.status === "checking" && (
            <div className="w-full max-w-sm text-sm text-[#525B6A]" data-testid="reset-checking">Verifying reset link…</div>
          )}

          {state.status === "invalid" && (
            <div className="w-full max-w-sm" data-testid="reset-invalid">
              <div className="md:hidden mb-8"><Logo /></div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-5">
                <AlertTriangle className="h-5 w-5 text-amber-700" />
              </div>
              <h2 className="text-3xl font-display font-bold text-[#0A0D14]">Link no longer valid.</h2>
              <p className="mt-3 text-sm text-[#525B6A]">{reasonCopy[state.reason] || reasonCopy.invalid} For your safety, reset links expire after 30 minutes and can only be used once.</p>
              <div className="mt-8 flex items-center gap-4">
                <Link to="/forgot-password" data-testid="reset-request-new" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2">Request new link <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/login" className="text-sm text-[#0047FF] font-medium">Back to log in</Link>
              </div>
            </div>
          )}

          {state.status === "ready" && (
            <form onSubmit={submit} className="w-full max-w-sm" data-testid="reset-form">
              <div className="md:hidden mb-8"><Logo /></div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              </div>
              <h2 className="text-3xl font-display font-bold text-[#0A0D14]">New password.</h2>
              <p className="mt-2 text-sm text-[#525B6A]">Resetting the password for <span className="text-[#0A0D14] font-medium">{state.email}</span>.</p>

              <div className="mt-8 space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#525B6A] mb-1 block">New password</label>
                  <input
                    required
                    minLength={8}
                    type="password"
                    data-testid="reset-p1"
                    className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
                    value={pw.p1}
                    onChange={(e) => setPw({ ...pw, p1: e.target.value })}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#525B6A] mb-1 block">Confirm new password</label>
                  <input
                    required
                    minLength={8}
                    type="password"
                    data-testid="reset-p2"
                    className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
                    value={pw.p2}
                    onChange={(e) => setPw({ ...pw, p2: e.target.value })}
                  />
                </div>
                <button
                  disabled={busy}
                  data-testid="reset-submit"
                  className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? "Updating…" : (<>Update password <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
